import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from 'src/api/client';
import { Helmet } from 'react-helmet-async';
import { create } from 'zustand';
import { CompanyProvider } from 'src/context/company-context';
import { Result } from 'src/antd';
import { TAllowedArcValue } from 'src/api/consts/CriptioArcValues';
import { T, useT } from '@transifex/react';
import { useQuery } from '@tanstack/react-query';
import { setTransifexLocale } from 'src/components/I18n';
import { setDatetimeLocale } from '../utils';
import { getCrmHostname } from './brandColorCache';
import { LoadingPage } from '../components/shared';
import { TBorderSize } from '../theme/borders';

type TValue = {
  id: string;
  name: string;
  phone_number: string;
  country: string;
  default_language: string;
  login_logo: string;
  login_method: 'none' | 'user' | 'bankid';
  login_method_user: 'pre_auth_search' | 'post_auth_search';
  datafordeler_enabled?: boolean;
  logo: string;
  primary_color: string;
  secondary_color: string;
  waste_fractions: [
    {
      id: string;
      name: string;
      waste_category: string;
      waste_substance: string;
      waste_types: [
        {
          id: string;
          name: string;
        },
      ];
    },
  ];
  crm_custom_text_login_title: string;
  crm_custom_text_login: string;
  page_description_settings: {
    title?: string;
    page: 'login' | 'home';
    sections: {
      title?: string;
      description?: string;
      link_label?: string;
      link_url?: string;
      background_image?: string;
      order?: number;
    }[];
  }[];
  crm_custom_text_home_title: string;
  crm_custom_text_home: string;
  crm_enable_container_notification_providers: string[];
  crm_enable_products?: boolean;
  authentication_urls: {
    acr_values: TAllowedArcValue;
    url: string;
    business: boolean;
    provider: 'mitid' | 'nemid' | 'bankid';
  }[];
  crm_pdf_footer?: string;
  main_portal_font_family?: string;
  secondary_portal_font_family?: string;
  main_portal_font?: string;
  secondary_portal_font?: string;
  background_image_for_home_page?: string;
  background_image_for_login_page?: string;
  border_radius?: TBorderSize;
  supported_languages?: string[];
};

export type TCompanyStore = {
  data: TValue;
  hostname: string;
};

const useCompanyStore = create<TCompanyStore>()(() => ({}) as TCompanyStore);

export const useCompany = (): TCompanyStore => useCompanyStore();

// --- TEMPORARY frontend feature-flag override -------------------------------
// The backend doesn't yet expose `crm_enable_products` on the company config,
// so we force it on for specific tenants here. Keyed on the resolved CRM
// hostname (see getCrmHostname). Only ever forces a flag ON — never off — so a
// real backend value of `true` still wins.
//
// DELETE this block (and the applyFrontendFlagOverrides call below) once the
// backend ships the real `crm_enable_products` flag.
const PRODUCTS_ENABLED_HOSTNAMES = new Set<string>(['']);

const applyFrontendFlagOverrides = (data: TValue, hostname: string): void => {
  if (PRODUCTS_ENABLED_HOSTNAMES.has(hostname)) {
    data.crm_enable_products = true;
  }
};
// ----------------------------------------------------------------------------

// Pads the company logo into a square data URL so the browser doesn't squash
// wide/tall logos when scaling to favicon size. Falls back to the raw URL if
// the image fails to load or CORS blocks the canvas readback.
const useSquareFaviconHref = (logoUrl: string | undefined): string => {
  const [padded, setPadded] = useState<{ src: string; href: string } | null>(
    null
  );

  useEffect(() => {
    if (!logoUrl) return undefined;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const scale = Math.min(size / img.width, size / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      try {
        setPadded({ src: logoUrl, href: canvas.toDataURL('image/png') });
      } catch {
        // tainted canvas — fall through to raw logo URL
      }
    };
    img.src = logoUrl;
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  if (padded && padded.src === logoUrl) return padded.href;
  return logoUrl ?? 'https://not-found.png';
};

const FaviconLink = ({ logo }: { logo: string | undefined }) => {
  const href = useSquareFaviconHref(logo);
  return (
    <Helmet>
      <link rel="icon" type="image/png" href={href} />
    </Helmet>
  );
};

export const CompanyStoreProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const t = useT();

  // Resolve the tenant once per mount via the shared resolver, so the company
  // fetch hostname and the startup spinner-colour cache key (src/index.tsx)
  // always agree.
  const hostname = useMemo(() => getCrmHostname(), []);

  const { isLoading, isError, error, data } = useQuery({
    queryKey: ['CompanyStoreProvider', 'GET'],
    // Company config is static per session — don't refire on reconnect/remount.
    staleTime: 60_000,
    queryFn: async () => {
      const d = await apiClient.get(`company/${hostname}`);
      if (d.data.id) {
        applyFrontendFlagOverrides(d.data as TValue, hostname);
        useCompanyStore.setState({ data: d.data as TValue, hostname });
        // Apply the tenant's default locale before children render, so the
        // first paint isn't in source English. We await Transifex's
        // translation fetch so `LoadingPage` covers the switch instead of
        // the user seeing an English-to-Danish flash. Errors are swallowed
        // — a failed translation fetch shouldn't block the company query.
        const initialLocale = (d.data as TValue).default_language;
        if (initialLocale) {
          try {
            await setTransifexLocale(initialLocale);
          } catch {
            /* ignore — fall back to source */
          }
          setDatetimeLocale(initialLocale);
        }
      }
      return d;
    },
  });

  if ((error as $TSFixMe)?.response?.data?.detail === 'Not Found') {
    // the domain is not set to any company.
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100%',
        }}
      >
        <Result
          status="404"
          title={
            <T
              _str="No DNS setup for hostname: {hostName}"
              hostName={hostname}
            />
          }
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100%',
        }}
      >
        <Result
          status="500"
          title={<T _str="Error occured: Please try later." />}
        />
      </div>
    );
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  const company = data?.data as TValue;

  return (
    <>
      <FaviconLink logo={company.logo} />
      <Helmet>
        <title>
          {t('Self-service - {companyName}', {
            companyName: company.name,
          }) || t('Citizen Portal')}
        </title>
      </Helmet>
      <CompanyProvider>{children}</CompanyProvider>
    </>
  );
};
